import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import AppleSignInButton from "@/components/AppleSignInButton";
import ProductCard from "@/components/ProductCard";
import TrustTierBadge from "@/components/TrustTierBadge";
import SellerAvatar from "@/components/SellerAvatar";
import AnimatedWaveHero from "@/components/AnimatedWaveHero";
import { getCategoryImage, CATEGORY_FALLBACK_IMAGE } from "@/lib/categoryImages";
import HomeStats from "@/components/home/HomeStats";
import HowItWorks from "@/components/home/HowItWorks";
import TrendingThisWeek from "@/components/home/TrendingThisWeek";
import WhyBuyersLove from "@/components/home/WhyBuyersLove";
import WhySellersLove from "@/components/home/WhySellersLove";
import GetTheAppSection from "@/components/home/GetTheAppSection";
import UpcomingStreams from "@/components/home/UpcomingStreams";
import HomeFAQ from "@/components/home/HomeFAQ";
import NewsletterSignup from "@/components/home/NewsletterSignup";
import BeaconSpotlight from "@/components/beacon/BeaconSpotlight";
import ColiseumRibbon from "@/components/beacon/ColiseumRibbon";
import HeadlinerStage from "@/components/beacon/HeadlinerStage";
import PulseRing from "@/components/beacon/PulseRing";
import VelocityLane from "@/components/beacon/VelocityLane";
import MomentumCarousel from "@/components/momentum/MomentumCarousel";
import UsernameReserveCard from "@/components/UsernameReserveCard";
import FounderLetter from "@/components/FounderLetter";
import MeetFounderFloater from "@/components/MeetFounderFloater";
import ComingSoonBanner from "@/components/ComingSoonBanner";
import SeoHead, { organizationLd, websiteLd, mobileAppLd, faqLd } from "@/components/SeoHead";
import {
  ArrowRightIcon,
  BroadcastIcon,
  SealCheckIcon,
  ShieldCheckIcon,
  UsersThreeIcon,
  HeartStraightIcon,
  LightningIcon,
  SparkleIcon,
  CoinsIcon,
} from "@phosphor-icons/react";
import FeeBarChart from "@/components/FeeBarChart";

export default function Landing() {
  const { user } = useAuth();
  const [featured, setFeatured] = useState([]);
  const [streams, setStreams] = useState([]);
  const [featuredStreams, setFeaturedStreams] = useState([]);
  const [applyStats, setApplyStats] = useState(null);
  const [beacon, setBeacon] = useState(null);
  const [categories, setCategories] = useState([
    "Sneakers",
    "Fragrances & Perfume",
    "Luxury Bags & Accessories",
    "Storage Unit Finds",
    "Trading Card Games",
    "Electronics",
    "Funko",
    "Rocks & Crystals",
    "Sports Cards",
    "Plants",
    "Women's Apparel",
    "Jewelry",
  ]);

  useEffect(() => {
    (async () => {
      try {
        const [feat, liv, featStreams, apStats, cats, bcn] = await Promise.all([
          api.get("/listings", { params: { limit: 8 } }),
          api.get("/streams", { params: { status: "live", limit: 4 } }),
          api.get("/featured/streams", { params: { limit: 4 } }),
          api.get("/seller-applications/stats"),
          api.get("/categories"),
          api.get("/beacon/spotlight").catch(() => ({ data: null })),
        ]);
        setFeatured(feat.data.items);
        setStreams(liv.data.items);
        setFeaturedStreams(featStreams.data.items);
        setApplyStats(apStats.data);
        setCategories(cats.data.categories || []);
        setBeacon(bcn?.data || null);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  // Real-time refresh of the founder-seats counter. Opens a WebSocket to
  // `/api/ws/founder-seats` so the number ticks down the instant an admin
  // approves a Founder Seller application (no polling lag). Falls back to
  // a 30s poll if the WS handshake fails (flaky network / corp proxy).
  useEffect(() => {
    let cancelled = false;
    let ws = null;
    let pollTimer = null;
    const refreshStats = async () => {
      try {
        const r = await api.get("/seller-applications/stats");
        if (!cancelled) setApplyStats(r.data);
      } catch (_) {}
    };
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const url = new URL(base);
      url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
      url.pathname = "/api/ws/founder-seats";
      ws = new WebSocket(url.toString());
      ws.onmessage = (ev) => {
        try {
          const d = JSON.parse(ev.data);
          if (d.type === "founder_seats.update" && d.stats && !cancelled) {
            setApplyStats(d.stats);
          }
        } catch { /* non-JSON ping */ }
      };
      ws.onerror = () => { pollTimer = setInterval(refreshStats, 30000); };
    } catch {
      pollTimer = setInterval(refreshStats, 30000);
    }
    const onFocus = () => refreshStats();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      cancelled = true;
      if (ws) { try { ws.close(); } catch {/*noop*/} }
      if (pollTimer) clearInterval(pollTimer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <SeoHead
        title="Bidorah — Live Auctions & Verified Marketplace App"
        description="Bidorah is the verified live-auction marketplace for collectors. Bid live on sneakers, trading cards, jewelry, fragrances and more from US photo-ID verified sellers. Download the Bidorah app on Google Play."
        canonical="https://bidorah.com/"
        keywords="bidorah, bidorah app, bidorah live, live auctions, live shopping, sneaker auctions, trading card auctions, sports cards, pokemon, fragrances, jewelry, luxury bags, US verified marketplace, live shopping app, founder seller, wholesale liquidation"
        jsonLd={[
          organizationLd(),
          websiteLd(),
          mobileAppLd(),
          faqLd([
            {
              q: "What is Bidorah?",
              a: "Bidorah is a verified live-auction marketplace for collectors. Every seller passes US photo-ID verification before listing, and wholesale buyers are kept strictly separate from retail.",
            },
            {
              q: "Is the Bidorah app free?",
              a: "Yes. Download the Bidorah app free from Google Play. There are no membership fees for buyers; sellers pay a small commission only when they sell.",
            },
            {
              q: "What can I buy on Bidorah?",
              a: "Sneakers, trading cards (Pokémon, sports cards, MTG), luxury bags & accessories, jewelry, fragrances, electronics, Funko, rocks & crystals, and storage-unit finds — all from US-verified sellers.",
            },
            {
              q: "How do live auctions work on Bidorah?",
              a: "Sellers go live with their inventory and run real-time auctions. Bid live, see the seller react, and the highest bidder when the timer ends wins. Live streaming is rolling out in waves to approved Founder Sellers.",
            },
            {
              q: "How do I become a seller on Bidorah?",
              a: "Apply for a Founder Seller seat through the in-app 'Apply to sell' flow. Approved Founder Sellers pay 0% commission for their first 14 days (starting the day you're approved) and get priority placement.",
            },
          ]),
        ]}
      />
      <Navbar />

      {/* Coming-Soon announcement — live shows rolling out in waves + iOS
          app launching on the App Store. Dismissible, persists per-user. */}
      <ComingSoonBanner />

      {/* Founder seats tension strip */}
      {applyStats && !applyStats.closed && (
        <Link
          to="/apply-to-sell"
          className="block bg-zinc-900 text-white hover:bg-zinc-800 transition-colors"
          data-testid="landing-tension-strip"
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-2.5 flex items-center gap-3 text-xs font-mono tracking-wider flex-wrap">
            <LightningIcon size={14} weight="fill" className="text-amber-400" />
            <span className="text-amber-400 font-bold">FOUNDER SELLER ROLLOUT</span>
            <span className="text-white/70">
              Only <b className="text-white">{applyStats.spots_left}</b> of{" "}
              {applyStats.seats_total} seats remaining — 0% fees for 14 days, Founder badge for
              life.
            </span>
            <span className="ml-auto text-white underline decoration-amber-400 decoration-2 underline-offset-4">
              Apply →
            </span>
          </div>
        </Link>
      )}

      {/* HERO */}
      <section className="relative border-b border-zinc-200 overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-50 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <p className="text-sm font-semibold text-[#1D78AB] mb-5" data-testid="hero-tagline">
                Live-Auction Commerce
              </p>
              <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.92] tracking-tighter text-zinc-900">
                Bid live.<br />
                <span className="text-[#1D78AB]">Win real.</span>
              </h1>
              <p className="mt-6 text-lg text-zinc-600 max-w-xl leading-relaxed">
                Bidorah is the live-auction marketplace where verified sellers run real-time streams
                and real buyers win real items. Retail and wholesale run in strictly separated lanes.
                No mixing. Ever.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/register" data-testid="hero-get-started">
                  <Button className="bg-[#1D78AB] hover:bg-[#165d85] text-white h-12 px-7 text-sm font-semibold tracking-wide rounded-full shadow-lg shadow-[#1D78AB]/20 hover:shadow-xl hover:shadow-[#1D78AB]/30 hover:-translate-y-0.5">
                    Get started <ArrowRightIcon size={16} className="ml-2" />
                  </Button>
                </Link>
                <Link to="/browse" data-testid="hero-browse">
                  <Button variant="outline" className="h-12 px-7 text-sm font-semibold tracking-wide rounded-full border-zinc-300 hover:border-[#1D78AB] hover:text-[#1D78AB]">
                    Browse the marketplace
                  </Button>
                </Link>
              </div>
              {/* One-tap "Continue with Google" — primary low-friction
                  onboarding path. Hidden once the visitor is already
                  signed in to avoid offering a second login on top of an
                  active session. Mirrors the same flow used in /login. */}
              {!user && (
                <div className="mt-4 max-w-xs space-y-3" data-testid="hero-google-cta-wrap">
                  <GoogleSignInButton
                    testid="hero-google-cta"
                    label="Continue with Google"
                  />
                  <AppleSignInButton testid="hero-apple-cta" />
                  <p className="mt-2 text-[11px] text-zinc-500 leading-relaxed">
                    No password. We never post or scrape your Google account.
                  </p>
                </div>
              )}
              <Link
                to="/why-bidorah"
                className="inline-flex items-center gap-2 mt-5 text-sm font-semibold text-[#1D78AB] hover:underline"
                data-testid="hero-why-bidorah"
              >
                See what makes Bidorah different <span className="text-base">→</span>
              </Link>
              <div className="mt-10 flex flex-wrap items-center gap-6 text-xs text-zinc-500">
                <div className="flex items-center gap-2">
                  <ShieldCheckIcon size={18} className="text-[#1D78AB]" />
                  US photo-ID verified sellers
                </div>
                <div className="flex items-center gap-2">
                  <SealCheckIcon size={18} className="text-[#1D78AB]" />
                  Segmented wholesale & retail
                </div>
                <div className="flex items-center gap-2">
                  <BroadcastIcon size={18} className="text-[#1D78AB]" />
                  Real-time bid feeds
                </div>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 aspect-[16/10] bg-zinc-900 text-white rounded-2xl overflow-hidden relative shadow-lg" data-testid="hero-stream-card">
                  <AnimatedWaveHero
                    title={streams[0]?.title || "Launch a stream. Get discovered."}
                    subtitle={streams[0]?.seller_name || "Become a verified seller"}
                  />
                </div>

                {/* Conversion widget — turn anonymous landing-page traffic
                    into qualified email leads via @handle reservations. */}
                <div className="col-span-2">
                  <UsernameReserveCard />
                </div>

                <div className="card-modern p-5">
                  <div className="overline text-zinc-500 mb-1">ACTIVE LISTINGS</div>
                  <div className="font-display font-black text-3xl text-[#1D78AB]">
                    {featured.length}+
                  </div>
                </div>
                <div className="card-modern p-5">
                  <div className="overline text-zinc-500 mb-1">LIVE STREAMS</div>
                  <div className="font-display font-black text-3xl text-[#1D78AB]">
                    {streams.length}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BIDORIA DEMO TEASER — between hero and policy banner. Prime real
          estate to drive curious visitors into the /demo experience. */}
      <section className="bg-zinc-900 border-y border-amber-500/20 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_50%,rgba(245,158,11,0.18),transparent_55%)] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-7 flex flex-wrap items-center justify-between gap-4 relative">
          <div className="flex items-center gap-3 min-w-0">
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 border border-amber-400/30 px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold text-amber-300">
              <SparkleIcon size={10} weight="fill" />
              New
            </span>
            <p className="text-base sm:text-lg font-semibold leading-tight">
              The only live auction app with{" "}
              <span className="text-amber-300">Bidoria.</span>{" "}
              <span className="text-zinc-400 font-normal hidden sm:inline">
                Hear it before you sign up.
              </span>
            </p>
          </div>
          <Link
            to="/demo"
            data-testid="hero-demo-cta"
            className="inline-flex items-center gap-2 rounded-full bg-amber-400 hover:bg-amber-300 text-black font-bold px-5 py-2.5 text-sm transition-colors shadow-lg shadow-amber-500/20 hover:shadow-xl"
          >
            Try the live demo
            <ArrowRightIcon size={14} weight="bold" />
          </Link>
        </div>
      </section>

      {/* EVERY % COUNTS — fee comparison vs named competitors. This is
          the seller-acquisition headliner: live commerce platforms
          publish their fees, ours undercuts every single one of them,
          and visually slamming that home converts. Bars are CSS-only
          and animate up on first mount. Numbers are public list rates
          (incl. payment processing where the competitor lumps it in). */}
      <section
        className="bg-zinc-950 text-white border-y border-amber-500/20 relative overflow-hidden"
        data-testid="landing-every-percent"
      >
        <div className="absolute inset-0 pointer-events-none bidorah-trinity-aurora" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16 sm:py-20 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* LEFT: pitch */}
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 px-3 py-1 text-[11px] uppercase tracking-widest text-emerald-300 font-bold mb-5">
              <CoinsIcon size={11} weight="fill" />
              Lowest fees in live commerce
            </span>
            <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.0]">
              Every <span className="text-emerald-300">%</span> counts.
            </h2>
            <p className="mt-5 text-base sm:text-lg text-white/75 max-w-xl leading-relaxed">
              Sell on Bidorah for <strong className="text-emerald-300">5%</strong> retail, <strong className="text-emerald-300">4.5%</strong> wholesale. That's <strong className="text-white">half</strong> of what Whatnot, eBay, StockX, and Mercari charge — every dollar over their fee floor lands in your pocket, not theirs.
            </p>
            <p className="mt-3 text-sm text-white/60 max-w-xl leading-relaxed">
              <span className="font-bold text-white">Founder Sellers pay 0%</span> commission for their first 14 days (starting the day you're approved). Tips are always 100% yours — Bidorah takes nothing.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to={user ? "/sell" : "/register"}
                className="inline-flex items-center gap-2 rounded-full bg-emerald-400 hover:bg-emerald-300 text-emerald-950 font-black px-6 py-3 text-sm transition-colors shadow-lg shadow-emerald-500/30 hover:shadow-xl"
                data-testid="landing-every-percent-cta"
              >
                Start selling for free
                <ArrowRightIcon size={14} weight="bold" />
              </Link>
              <Link
                to="/why-bidorah"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 hover:border-white/30 px-6 py-3 text-sm font-bold text-white/85 hover:text-white transition-colors"
              >
                See the full breakdown
              </Link>
            </div>
          </div>

          {/* RIGHT: bar-chart visual */}
          <FeeBarChart />
        </div>
      </section>

      {/* HOW IT WORKS — right after the hero to orient first-time visitors */}
      <div data-testid="how-it-works" className="hidden md:block"><HowItWorks /></div>

      {/* MOMENTUM CAROUSEL — premium 3D coverflow of live shows.
          Mobile-first; designed to beat Whatnot's "Shows with Momentum"
          via Vault Coverflow + Spotlight Stage + Live Aura layered. */}
      <MomentumCarousel />

      {/* ============================================================
          THE BEACON — moving spotlight system. All five visual concepts
          stacked top-to-bottom so every kind of seller-incentive cue
          fires across the homepage:
            1. Beacon wall (cinematic searchlight)
            2. Coliseum ribbon (ESPN ticker)
            3. Headliner stage (Coachella billing of top 3)
            4. Pulse ring (orbiting constellation)
            5. Velocity lane (racetrack tied to growth %)
          They all read from the same `/api/beacon/spotlight` payload
          so the data is consistent across visuals.
          ============================================================ */}
      {beacon && beacon.headliners?.length ? (
        <>
          <BeaconSpotlight data={beacon} />
          <ColiseumRibbon data={beacon} />
          <HeadlinerStage data={beacon} />
          <PulseRing data={beacon} />
          {beacon.velocity?.length ? <VelocityLane data={beacon} /> : null}
        </>
      ) : null}

      {/* BY THE NUMBERS — live DB stats for trust */}
      <div data-testid="home-stats" className="hidden md:block"><HomeStats /></div>

      {/* TRENDING THIS WEEK — FOMO engine for categories + top listings */}
      <div data-testid="trending-this-week" className="hidden md:block"><TrendingThisWeek /></div>

      {/* COVERING EVERY LANE — categories with pictures + text. On mobile
          this is collapsed into the "Discover more" grid below, so we hide
          the full visual carpet here on small screens. */}
      <section
        className="hidden md:block max-w-7xl mx-auto px-6 lg:px-8 pt-12 sm:pt-16 pb-6"
        data-testid="covering-every-lane"
      >
        <div className="mb-6 sm:mb-8" data-testid="categories-grid-heading">
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter">
            Covering every lane
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="categories-grid">
          {categories.map((c) => (
            <Link
              key={c}
              to={`/browse?category=${encodeURIComponent(c)}`}
              className="group block border border-zinc-200 bg-white overflow-hidden hover-lift rounded-xl"
              data-testid={`home-cat-${c}`}
            >
              <div className="aspect-[3/2] bg-zinc-100 relative overflow-hidden">
                <img
                  src={getCategoryImage(c)}
                  alt={c}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  onError={(e) => {
                    if (e.currentTarget.src.indexOf(CATEGORY_FALLBACK_IMAGE) === -1) {
                      e.currentTarget.src = CATEGORY_FALLBACK_IMAGE;
                    }
                  }}
                />
              </div>
              <div className="p-2.5 sm:p-4 flex items-center justify-between gap-2">
                <p className="font-display font-semibold text-xs sm:text-base leading-tight">
                  {c}
                </p>
                <ArrowRightIcon
                  size={14}
                  className="text-zinc-400 group-hover:text-[#1D78AB] flex-shrink-0"
                />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED (INSTANT TIER) STREAMS */}
      <div data-testid="featured-founder-streams">
      {featuredStreams.length > 0 && (
        <section className="relative border-b border-zinc-200 bg-[radial-gradient(circle_at_top_right,#fef3c7,transparent_60%)]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <LightningIcon size={18} weight="fill" className="text-amber-500" />
                  <p className="text-sm font-bold text-amber-700">Featured · Instant Tier</p>
                </div>
                <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter mt-2">
                  Top sellers, live right now
                </h2>
                <p className="text-sm text-zinc-600 mt-1">
                  500+ completed sales. Instant payouts. Front of the line.
                </p>
              </div>
              <Link to="/live" className="text-sm font-semibold text-[#1D78AB]" data-testid="featured-all-link">
                All live streams →
              </Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {featuredStreams.map((s) => (
                <Link
                  key={s.id}
                  to={`/stream/${s.id}`}
                  data-testid={`featured-stream-${s.id}`}
                  className="group card-modern hover-lift relative ring-2 ring-amber-300"
                >
                  <div className="aspect-video bg-zinc-900 relative grid place-items-center text-white overflow-hidden">
                    <BroadcastIcon size={40} className="text-zinc-600" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute top-3 left-3 pill pill-live">
                      <span className="h-1.5 w-1.5 rounded-full bg-white live-dot" /> LIVE
                    </div>
                    <div className="absolute top-3 right-3 pill pill-instant">
                      <LightningIcon size={10} weight="fill" /> INSTANT
                    </div>
                    <div className="absolute bottom-3 right-3 pill pill-dark">
                      <UsersThreeIcon size={10} /> {s.viewer_count || 0}
                    </div>
                    <div className="absolute -bottom-5 left-4 z-10">
                      <SellerAvatar name={s.seller_name} picture={s.seller_picture} size={44} />
                    </div>
                  </div>
                  <div className="p-4 pt-5 pl-[72px]">
                    <p className="font-display font-semibold text-sm line-clamp-1 group-hover:text-[#1D78AB] transition-colors">
                      {s.title}
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">{s.seller_name} · {s.category}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
      </div>

      {/* LIVE STREAMS */}
      <div data-testid="live-now">
      {streams.length > 0 && (
        <section className="bg-zinc-50 border-y border-zinc-200">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
            <div className="flex items-end justify-between mb-10">
              <div>
                <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">
                  Live auctions, happening now
                </h2>
              </div>
              <Link to="/live" className="text-sm font-semibold text-[#1D78AB]" data-testid="all-live-link">
                All streams →
              </Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {streams.map((s) => (
                <Link
                  key={s.id}
                  to={`/stream/${s.id}`}
                  data-testid={`stream-card-${s.id}`}
                  className="group card-modern hover-lift"
                >
                  <div className="aspect-video bg-zinc-900 relative grid place-items-center text-white overflow-hidden">
                    <BroadcastIcon size={40} className="text-zinc-600" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute top-3 left-3 pill pill-live">
                      <span className="h-1.5 w-1.5 rounded-full bg-white live-dot" /> LIVE
                    </div>
                    <div className="absolute bottom-3 right-3 pill pill-dark">
                      <UsersThreeIcon size={10} /> {s.viewer_count || 0}
                    </div>
                    <div className="absolute -bottom-5 left-4 z-10">
                      <SellerAvatar name={s.seller_name} picture={s.seller_picture} size={44} />
                    </div>
                  </div>
                  <div className="p-4 pt-5 pl-[72px]">
                    <p className="font-display font-semibold text-sm line-clamp-1 group-hover:text-[#1D78AB] transition-colors">
                      {s.title}
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">{s.seller_name} · {s.category}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
      </div>

      {/* MOBILE-ONLY: "Discover more" CTA grid — replaces the long
          tail of secondary sections on first launch so Capacitor users
          aren't asked to scroll through 12+ panels. Each card deep-
          links to the dedicated page with that content. Hidden on
          desktop (md+) where the full landing reads naturally. */}
      <section
        className="md:hidden bg-zinc-50 border-y border-zinc-200 px-4 py-10"
        data-testid="mobile-discover-grid"
      >
        <h2 className="font-display font-black text-2xl tracking-tighter mb-1">
          Discover more
        </h2>
        <p className="text-sm text-zinc-500 mb-6">
          Pick a path — each one opens on its own page.
        </p>
        <div className="grid grid-cols-2 gap-3">
          {[
            { to: "/why-bidorah", label: "How it works", sub: "4-step verified flow", testid: "discover-how-it-works" },
            { to: "/browse", label: "Trending", sub: "Hot drops + auctions", testid: "discover-trending" },
            { to: "/categories", label: "Categories", sub: "Sneakers · Cards · Jewelry", testid: "discover-categories" },
            { to: "/letter", label: "From the founder", sub: "Why we built Bidorah", testid: "discover-founder" },
            { to: "/apply-to-sell", label: "Sell on Bidorah", sub: "Apply for Founder seat", testid: "discover-apply" },
            { to: "/policy", label: "FAQ + policies", sub: "Rules, refunds, segregation", testid: "discover-faq" },
          ].map((c) => (
            <Link
              key={c.to}
              to={c.to}
              className="rounded-2xl bg-white border border-zinc-200 p-4 hover:border-[#1D78AB] active:bg-zinc-50 transition-colors"
              data-testid={c.testid}
            >
              <div className="font-display font-black text-sm text-zinc-900 leading-tight">
                {c.label}
              </div>
              <div className="text-[11px] text-zinc-500 mt-1 leading-snug">
                {c.sub}
              </div>
              <div className="text-[#1D78AB] mt-2 text-xs font-bold">
                Open →
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* DESKTOP-ONLY: the full long-form landing tail. Mobile users
          go through the discover grid above instead. */}
      <div className="hidden md:block">

      {/* FEATURED LISTINGS */}
      <div data-testid="fixed-price-listings">
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <div className="flex items-end justify-between mb-10">
            <div>
              <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">
                Trending listings
              </h2>
            </div>
            <Link to="/browse" className="text-sm font-semibold text-[#1D78AB]" data-testid="all-listings-link">
              Browse all →
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {featured.map((item) => (
              <ProductCard key={item.id} listing={item} />
            ))}
          </div>
        </section>
      )}
      </div>

      {/* HOW IT WORKS */}
      <section className="bg-zinc-50 border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
          {/* POLICY BANNER — relocated here directly above "Four steps. No
              exceptions." so the segregation rule reads as the rationale for
              the four-step gate that follows. Was previously at top of page. */}
          <div
            className="mb-12 -mx-6 lg:-mx-8 bg-zinc-900 text-white"
            data-testid="landing-policy-banner"
          >
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-5 flex flex-wrap items-center gap-4 justify-between">
              <div className="flex items-center gap-3">
                <ShieldCheckIcon size={22} className="text-amber-400" />
                <p className="text-sm">
                  <span className="overline text-amber-400 mr-2">POLICY</span>
                  Wholesalers cannot sell to retail customers. No exceptions. No mixed carts.
                </p>
              </div>
              <Link to="/policy" className="text-sm text-white underline" data-testid="policy-link-howitworks">
                Read the policy →
              </Link>
            </div>
          </div>

          <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter mb-12">
            Four steps. No exceptions.
          </h2>
          <div className="grid md:grid-cols-4 gap-5">
            {[
              { n: "01", t: "Pick your lane", d: "Retail buyer, retail seller, wholesale buyer, or wholesale seller. Permanent at signup." },
              { n: "02", t: "Verify ID", d: "Upload your US government photo ID. Fast review. Required for all activity." },
              { n: "03", t: "Go live or browse", d: "Sellers launch a stream or list BIN. Buyers bid in real time or tap Buy Now." },
              { n: "04", t: "Win. Pay. Ship.", d: "Stripe checkout on win. Segment-checked. Seller ships. Done." },
            ].map((s) => (
              <div key={s.n} className="card-modern p-6">
                <div className="font-mono text-xs text-[#1D78AB] mb-3">STEP {s.n}</div>
                <div className="font-display font-bold text-lg mb-2">{s.t}</div>
                <p className="text-sm text-zinc-600 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY BUYERS LOVE US */}
      <div data-testid="why-buyers-love"><WhyBuyersLove /></div>

      {/* UPCOMING SCHEDULED STREAMS */}
      <div data-testid="upcoming-streams"><UpcomingStreams /></div>

      {/* WHY SELLERS LOVE US — leads into Apply-to-Sell */}
      <div data-testid="why-sellers-love"><WhySellersLove /></div>

      {/* FROM THE FOUNDER — short excerpt + CTA into the full /letter.
          The id is the smooth-scroll target for the floating "Meet the
          founder" CTA pinned to the bottom of the viewport. */}
      <div id="meet-the-founder">
        <FounderLetter variant="excerpt" />
      </div>

      {/* GET THE APP */}
      <div data-testid="home-get-app"><GetTheAppSection /></div>

      {/* FAQ */}
      <div data-testid="home-faq"><HomeFAQ /></div>

      {/* NEWSLETTER CAPTURE — last chance conversion */}
      <div data-testid="newsletter-signup"><NewsletterSignup /></div>

      </div>{/* /hidden md:block — desktop long-form tail */}

      <Footer />

      {/* Floating "Meet the founder" CTA — quietly nudges first-time visitors
          to the founder excerpt for a trust boost; auto-hides once they
          scroll past it and is dismissible for the session. */}
      <MeetFounderFloater />
    </div>
  );
}
